import { jsPDF } from "jspdf";
import { brl } from "@/lib/format";
import { addPdfLogo } from "@/lib/useLogo";

const fmt = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
const ascii = (s) => String(s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// Balancete do período para a contabilidade: fixas/variáveis por categoria, faturamento, resultado e lançamentos.
export function buildBalancetePdf({ periodLabel, baseLabel, despesas, receitas, logo }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  addPdfLogo(doc, logo);
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 40;
  let y = M;
  const row = (l, v, o = {}) => {
    if (y > H - 50) { doc.addPage(); y = M; }
    doc.setFont("helvetica", o.bold ? "bold" : "normal");
    doc.setFontSize(o.size || 9);
    doc.setTextColor(...(o.color || [40, 40, 40]));
    doc.text(ascii(l), o.x ?? M, y);
    if (v != null) doc.text(ascii(v), W - M, y, { align: "right" });
    y += o.gap ?? 14;
  };
  const rule = () => { doc.setDrawColor(210); doc.line(M, y - 8, W - M, y - 8); y += 4; };
  const sum = (a) => a.reduce((s, e) => s + (Number(e.value) || 0), 0);
  const byCat = (a) => Object.entries(a.reduce((m, e) => ({ ...m, [e.category]: (m[e.category] || 0) + e.value }), {})).sort((x, z) => z[1] - x[1]);

  row("Central do Courier - Balancete do periodo", null, { bold: true, size: 15, gap: 20, color: [20, 20, 20] });
  row(`Periodo: ${periodLabel}    Base: ${baseLabel}`, null, { size: 10, color: [90, 90, 90], gap: 22 });
  rule();
  const fixas = despesas.filter((e) => e.type === "fixa");
  const variaveis = despesas.filter((e) => e.type !== "fixa");
  [["DESPESAS FIXAS", fixas], ["DESPESAS VARIAVEIS", variaveis]].forEach(([title, list]) => {
    row(title, brl(sum(list)), { bold: true, size: 11, color: [30, 64, 120], gap: 16 });
    if (!list.length) row("    Nenhuma despesa", null, { color: [150, 150, 150] });
    byCat(list).forEach(([c, v]) => row(`    ${c}`, brl(v)));
    y += 8;
  });
  row("TOTAL DE DESPESAS", brl(sum(despesas)), { bold: true, size: 11, gap: 22 });
  rule();
  row("FATURAMENTO", brl(sum(receitas)), { bold: true, size: 11, color: [16, 122, 87], gap: 16 });
  if (!receitas.length) row("    Nenhum faturamento lancado", null, { color: [150, 150, 150] });
  byCat(receitas).forEach(([c, v]) => row(`    ${c}`, brl(v)));
  y += 8;
  rule();
  const res = sum(receitas) - sum(despesas);
  row("RESULTADO LIQUIDO (faturamento - despesas)", brl(res), { bold: true, size: 12, color: res >= 0 ? [16, 122, 87] : [190, 50, 50], gap: 26 });

  row("LANCAMENTOS", null, { bold: true, size: 11, gap: 16 });
  row("Data", null, { bold: true, size: 8, gap: 0 });
  row("Descricao", null, { bold: true, size: 8, x: M + 60, gap: 0 });
  row("Categoria", null, { bold: true, size: 8, x: M + 270, gap: 0 });
  row("Tipo", "Valor", { bold: true, size: 8, x: M + 390, gap: 12 });
  [...receitas.map((e) => ({ ...e, t: "Receita" })), ...despesas.map((e) => ({ ...e, t: e.type === "fixa" ? "Fixa" : "Variavel" }))].forEach((e) => {
    row(e.auto ? "Periodo" : fmt(e.date), null, { size: 8, gap: 0 });
    row(String(e.description).slice(0, 40), null, { size: 8, x: M + 60, gap: 0 });
    row(String(e.category).slice(0, 22), null, { size: 8, x: M + 270, gap: 0 });
    row(e.t, `${e.t === "Receita" ? "" : "- "}${brl(e.value)}`, { size: 8, x: M + 390, gap: 12 });
  });
  doc.setFontSize(8); doc.setTextColor(150, 150, 150);
  doc.text(`Gerado em ${new Date().toLocaleString("pt-BR")}`, M, H - 25);
  return doc;
}
